import React, { useState, useEffect, useContext } from "react";
import { useSearchParams } from "react-router-dom";
import LayoutWrapper from "../components/LayoutWrapper";
import HeaderBar from "../components/HeaderBar";
import { ThemeContext } from "../context/ThemeContext";
import getCurrentSkin from "../utils/getCurrentSkin";
import axios from "axios";

const StockDetails = () => {
  const { isDark, skin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);

  const [searchParams] = useSearchParams();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Extract query params
  const branchId = searchParams.get("branchid");
  const itemId = searchParams.get("itemid");
  const fromDate = searchParams.get("fromdate");
  const toDate = searchParams.get("todate");

  useEffect(() => {
    if (branchId && itemId && fromDate && toDate) {
      fetchStockDetails();
    }
  }, [branchId, itemId, fromDate, toDate]);

  const fetchStockDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        "https://www.nissi-india.com/api/stock_summaryViewRpt",
        {
          params: {
            stockBranchId: branchId,
            stockItemId: itemId,
            stockFromDate: fromDate,
            stockToDate: toDate,
          },
        }
      );
      setData(response.data.data || []);
      setError(null);
    } catch (err) {
      console.error("Fetch failed", err);
      setError("Failed to load stock details");
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <LayoutWrapper>
      <HeaderBar title="INDIVIDUAL STOCK SUMMARY" />

      <div
        className={`p-6 mt-6 rounded-2xl shadow-xl border ${
          isDark
            ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-black"
            : `bg-white ${currentSkin.glass} text-black`
        }`}
      >
        {loading ? (
          <div className="text-center">Loading...</div>
        ) : error ? (
          <div className="text-center text-red-500">{error}</div>
        ) : data.length === 0 ? (
          <div className="text-center text-gray-500">No Records Found!</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border text-sm">
              <thead>
                <tr className="bg-blue-900 text-white text-center">
                  <th rowSpan="2" className="border px-2 py-1">Sl No</th>
                  <th rowSpan="2" className="border px-2 py-1">Branch</th>
                  <th rowSpan="2" className="border px-2 py-1">Item Code</th>
                  <th rowSpan="2" className="border px-2 py-1">Item Name</th>
                  <th rowSpan="2" className="border px-2 py-1">UOM</th>

                  {/* Group headers */}
                  <th colSpan="3" className="border px-2 py-1">Opening Stock</th>
                  <th colSpan="3" className="border px-2 py-1">GRN</th>
                  <th colSpan="3" className="border px-2 py-1">STNI</th>
                  <th colSpan="3" className="border px-2 py-1">SR</th>
                  <th colSpan="3" className="border px-2 py-1">SI</th>
                  <th colSpan="3" className="border px-2 py-1">STNO</th>
                  <th colSpan="3" className="border px-2 py-1">Closing Stock</th>
                </tr>
                <tr className="bg-blue-700 text-white text-center">
                  {Array(7).fill(null).map((_, idx) => (
                    <React.Fragment key={idx}>
                      <th className="border px-2 py-1">Qty</th>
                      <th className="border px-2 py-1">Rate</th>
                      <th className="border px-2 py-1">Value</th>
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((item, index) => (
                  <tr key={index} className="text-center">
                    <td className="border px-2 py-1">{index + 1}</td>
                    <td className="border px-2 py-1">{item.branchName}</td>
                    <td className="border px-2 py-1">{item.itemCode}</td>
                    <td className="border px-2 py-1">{item.itemName}</td>
                    <td className="border px-2 py-1">{item.uomName}</td>

                    {/* Opening Stock */}
                    <td className="border px-2 py-1">{item.openingStockQty}</td>
                    <td className="border px-2 py-1">{item.openingStockRate}</td>
                    <td className="border px-2 py-1">{item.openingStockValue}</td>

                    {/* GRN */}
                    <td className="border px-2 py-1">{item.grnQty}</td>
                    <td className="border px-2 py-1">{item.grnRate}</td>
                    <td className="border px-2 py-1">{item.grnValue}</td>

                    {/* STNI */}
                    <td className="border px-2 py-1">{item.stniQty}</td>
                    <td className="border px-2 py-1">{item.stniRate}</td>
                    <td className="border px-2 py-1">{item.stniValue}</td>

                    {/* SR */}
                    <td className="border px-2 py-1">{item.srQty}</td>
                    <td className="border px-2 py-1">{item.srRate}</td>
                    <td className="border px-2 py-1">{item.srValue}</td>

                    {/* SI */}
                    <td className="border px-2 py-1">{item.siQty}</td>
                    <td className="border px-2 py-1">{item.siRate}</td>
                    <td className="border px-2 py-1">{item.siValue}</td>

                    {/* STNO */}
                    <td className="border px-2 py-1">{item.stnoQty}</td>
                    <td className="border px-2 py-1">{item.stnoRate}</td>
                    <td className="border px-2 py-1">{item.stnoValue}</td>

                    {/* Closing */}
                    <td className="border px-2 py-1">{item.closingStockQty}</td>
                    <td className="border px-2 py-1">{item.closingStockRate}</td>
                    <td className="border px-2 py-1">{item.closingStockValue}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </LayoutWrapper>
  );
};

export default StockDetails;
